import React, { useMemo, useState } from 'react';

import {
  TextInput,
  Flex,
  Label,
  Icon,
  Tag,
  Text,
  LssProp,
  Loading,
  Button
} from '@lucidhq/lucidium';

import { useErrorDialogProvider } from '~/components/Shared/ErrorDialogProvider';
import { TemplateQuestion } from '~/components/Shared/hooks/useFetchTemplateQuestions';
import { useMutateGeneratedAnswers } from '~/components/Shared/hooks/useMutateGeneratedAnswers';
import { t } from '~/utils/i18n';
import {
  EnhancedTemplateQuestion,
  findVariationsLimit,
  formatAnswerVariations,
  getUniqueAnswers
} from '~/utils/templateQuestions';

import { useCampaignManager } from '../../CampaignManagerProvider';
import { useStandardQuestionsProvider } from '../StandardQuestionsStep/StandardQuestionsProvider';
import { AIStarIcon } from './AIStarIcon';

const tagLss: LssProp = {
  borderColor: 'secondary500',
  color: 'secondary500',
  mr: 'xs',
  mb: 'xs',
  py: 'xs',
  height: 'fit-content',
  alignItems: 'center'
};

const tagTextLss: LssProp = {
  fontSize: '14px',
  textOverflow: 'ellipsis',
  fontWeight: 'regular',
  textAlign: 'center'
};

const buttonLss: LssProp = {
  display: 'flex',
  padding: '0.675rem 0.5rem',
  width: '15rem',
  alignItems: 'center',
  height: 'fit-content'
};

type TextInterpretedAnswerOptionProps = {
  readOnly: boolean;
  question: EnhancedTemplateQuestion;
  onSave: (data: Partial<TemplateQuestion>) => void;
};

export const TextInterpretedAnswerOption = ({
  readOnly,
  question,
  onSave
}: TextInterpretedAnswerOptionProps) => {
  const answersCopy = [...question.answers];
  const [variations, setVariations] = useState<string[]>(answersCopy);
  const [value, setValue] = useState<string>('');
  const [isDisabled, setDisabled] = useState<boolean>(false);
  const { hasMaxVariations } = useMemo(
    () => findVariationsLimit(question.answers),
    [question.answers]
  );
  const [hasReachedLimit, setLimit] = useState<boolean>(hasMaxVariations);
  const [isDirty, setDirty] = useState<boolean>(false);
  const [isValid, setValid] = useState<boolean>(false);

  const { managedCampaign } = useCampaignManager();
  const { setError } = useErrorDialogProvider();
  const { hasKPIErrors } = useStandardQuestionsProvider();

  const handleRemove = (item: string) => {
    const updatedAnswers = variations.filter(val => val !== item);
    const updatedTargetIndices = updatedAnswers.map((_, index) => index);

    if (hasReachedLimit) setLimit(false);

    onSave({
      answers: updatedAnswers,
      multi_target_option_index: updatedTargetIndices
    });
    setVariations(updatedAnswers);
  };

  const campaignId = managedCampaign?.details?.campaign_id;
  const kpiType = question?.kpi_type;

  const { mutate, isLoading } = useMutateGeneratedAnswers({
    campaignId,
    kpiType
  });

  const handleGenerateAnswers = () => {
    mutate(
      {},
      {
        onSuccess: data => {
          const answers = getUniqueAnswers(question.answers, data?.items);
          const {
            answerVariations,
            hasMaxVariations,
            multiTargetIndices
          } = findVariationsLimit(answers);

          onSave({
            answers: answerVariations,
            multi_target_option_index: multiTargetIndices
          });
          setVariations(answers);
          setLimit(hasMaxVariations);
          // This sets a limit of 1 request for generating AI answers per KPI instance
          setDisabled(true);
        },
        onError: () => {
          setError(t('errors.generalApiError'));
        }
      }
    );
  };

  const hasInputError = hasKPIErrors && isDirty && !isValid && !isLoading;

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !!value.length) {
      const formattedAnswers = formatAnswerVariations(value);
      const answers = getUniqueAnswers(question.answers, formattedAnswers);
      const {
        answerVariations,
        hasMaxVariations,
        multiTargetIndices
      } = findVariationsLimit(answers);

      onSave({
        answers: answerVariations,
        multi_target_option_index: multiTargetIndices
      });
      setVariations(answerVariations);
      setLimit(hasMaxVariations);
      setValue('');
    }
  };

  return (
    <Flex
      lss={{
        flexDirection: 'column',
        width: '100%',
        maxWidth: readOnly ? '32rem' : 'none',
        marginTop: !readOnly ? '1.625rem' : '0.5rem'
      }}
    >
      {!readOnly && (
        <>
          <Label required>
            {t('campaignManager.survey.standardQuestions.questionCard.answer')}
          </Label>
          <Flex lss={{ flexDirection: 'row', gap: '1rem' }}>
            <Button
              palette="secondary"
              type="button"
              onClick={() => handleGenerateAnswers()}
              scale="md"
              lss={buttonLss}
              disabled={isLoading || isDisabled || hasReachedLimit}
            >
              {isLoading ? (
                <Loading spinnerSize="md" />
              ) : (
                <>
                  <Icon as={AIStarIcon} />
                  {t('campaignManager.buttonTexts.generateVariations')}
                </>
              )}
            </Button>
            <TextInput
              placeholder={t(
                `forms.placeholders.${
                  hasReachedLimit
                    ? 'disabledAnswerVariations'
                    : 'textAnswerVariations'
                }`
              )}
              inputLss={{ py: 'md', mb: 'xxs' }}
              containerLss={{ gap: '0.25rem' }}
              value={value}
              disabled={isLoading || hasReachedLimit}
              error={hasInputError ? t('forms.errors.answers') : ''}
              onChange={e => setValue(e.target.value)}
              onBlur={e => {
                setDirty(true);
                setValid(e.target.value !== '');
              }}
              onKeyPress={handleKeyPress}
            />
          </Flex>
        </>
      )}
      <Flex
        lss={{
          flexDirection: 'row',
          gap: '0.25rem',
          pt: !readOnly ? 'lg' : 'none',
          flexWrap: 'wrap'
        }}
      >
        {!!variations.length &&
          variations.map(item => {
            return (
              <Tag
                key={item}
                palette="primary"
                textScale="control"
                lss={tagLss}
                isRound
                hasOutline
                {...(!readOnly && {
                  onRemove: () => handleRemove(item)
                })}
              >
                <Text lss={tagTextLss}>{item}</Text>
              </Tag>
            );
          })}
      </Flex>
    </Flex>
  );
};
